import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import TextInput from '../common/TextInput';
import SelectInput from '../common/SelectInput';
import Button from '../common/Button';
import DeleteUserConfirmModal from '../modal/DeleteuserConfirmModal';
import { PermissionStatus, UserState } from '../../types/users';
import { useTranslation } from 'react-i18next';
import { DELETE_USER, UPDATE_USER } from '../../graphql/mutations';

interface EditUserProps {
    selectedUser: UserState;
    closeEditForm: () => void;
}

const EditUser: React.FC<EditUserProps> = ({ selectedUser, closeEditForm }) => {
    const { t } = useTranslation('users');

    const [updateUserMutation] = useMutation(UPDATE_USER);
    const [deleteUserMutation] = useMutation(DELETE_USER);

    const [username, setUsername] = useState<string>('');
    const [department, setDepartment] = useState<string>('');
    const [job, setJob] = useState<string>('');
    const [permission, setPermission] = useState<PermissionStatus>('admin');

    const [isShowDeleteUserConfirmModal, setIsShowDeleteUserConfirmModal] = useState<boolean>(false);

    useEffect(() => {
        setUsername(selectedUser.name);
        setDepartment(selectedUser.division);
        setJob(selectedUser.position);
        setPermission(selectedUser.role);
    }, [selectedUser])


    const permissionList = useMemo(() => [
        { value: 'admin', label: t('admin') },
        { value: 'general', label: t('general') }
    ], []);

    const handleSetPermission = useCallback((value: string) => {
        if (value === 'admin' || value === 'general') setPermission(value);
    }, []);

    const handleEditUser = () => {
        updateUserMutation({
            variables: {
                id: selectedUser.id,
                name: username,
                email: selectedUser.position,
                division: department,
                position: job,
                role: permission,
            }
        }).then((res) => {
            console.log(res);
            closeEditForm();
        });
    };

    const handleDeleteUser = (id: number) => {
        deleteUserMutation({
            variables: {
                id: id
            }
        }).then(() => {
            closeEditForm();
        }).catch((error: any) => {
            console.error('Error deleting user:', error);
            alert('Failed to delete user. Please try again.');
        });
    };

    return (
        <div>
            <DeleteUserConfirmModal
                isOpen={isShowDeleteUserConfirmModal}
                onClose={() => setIsShowDeleteUserConfirmModal(false)}
                onConfirm={() => handleDeleteUser(selectedUser.id)}
            />
            <h3>{t('user edit')}</h3>
            <TextInput
                label={t('username')}
                inputValue={username}
                onChange={setUsername}
            />
            <TextInput
                label={t('department')}
                inputValue={department}
                onChange={setDepartment}
            />
            <TextInput
                label={t('job')}
                inputValue={job}
                onChange={setJob}
            />
            <TextInput
                label={t('mail address')}
                inputValue={selectedUser.email}
                onChange={() => {}}
                readonly={true}
            />
            <SelectInput
                label={t('permission')}
                options={permissionList}
                selectedValue={permission}
                onChange={handleSetPermission}
            />
            <div className='flex flex-row justify-between'>
                <Button label={t('edit')} onClick={handleEditUser} />
                <Button label={t('delete')} onClick={() => setIsShowDeleteUserConfirmModal(true)} />
            </div>
        </div>
    );
};

export default EditUser;
